import React, { useState } from 'react';
import { Show, TopToolbar, ListButton, useRecordContext, useGetOne } from 'react-admin';
import { Card, CardContent, Typography, Box, Chip, Grid, Divider, Stack, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  Payment as PaymentIcon,
  Receipt as InvoiceIcon,
  Person as PersonIcon,
  AttachMoney as MoneyIcon,
  CalendarToday as DateIcon,
  Note as NoteIcon,
  CreditCard as CreditCardIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { BackButton } from '../../components/BackButton';
import { NavigationBreadcrumbs, BreadcrumbItem } from '../../components/NavigationBreadcrumbs';
import { useBrand } from '../../components/ServerBrandProvider';
import { PaymentEditDialog } from '../../components/dialogs/PaymentEditDialog';

const ShowActions = ({ onEdit }: { onEdit: () => void }) => (
  <TopToolbar>
    <BackButton 
      fallbackPath="/crm/payments" 
      variant="icon" 
      tooltip="Back to payments list"
    />
    <ListButton />
    <Button onClick={onEdit} startIcon={<EditIcon />} variant="contained" color="primary">
      Edit
    </Button>
  </TopToolbar>
);

const PaymentMethodChip = ({
  record,
}: {
  record?: { paymentMethod?: string } & Record<string, unknown>;
}) => {
  if (!record?.paymentMethod) {
    return <span>-</span>;
  }

  const getMethodColor = (method: string) => {
    switch (method?.toLowerCase()) {
      case 'cash':
        return 'success';
      case 'check':
        return 'info';
      case 'credit_card':
      case 'creditcard':
        return 'primary';
      case 'bank_transfer':
      case 'banktransfer':
        return 'secondary';
      case 'paypal':
        return 'warning';
      case 'other':
        return 'default';
      default:
        return 'default';
    }
  };

  const getMethodLabel = (method: string) => {
    switch (method?.toLowerCase()) {
      case 'credit_card':
      case 'creditcard':
        return 'Credit Card';
      case 'bank_transfer':
      case 'banktransfer':
        return 'Bank Transfer';
      case 'cash':
        return 'Cash';
      case 'check':
        return 'Check';
      case 'paypal':
        return 'PayPal';
      case 'other':
        return 'Other';
      default:
        return method?.charAt(0).toUpperCase() + method?.slice(1) || 'Unknown';
    }
  };

  return (
    <Chip
      label={getMethodLabel(record.paymentMethod)}
      color={
        getMethodColor(record.paymentMethod) as
          | 'default'
          | 'primary'
          | 'secondary'
          | 'success'
          | 'info'
          | 'warning'
          | 'error'
      }
      size="small"
    />
  );
};

const PaymentSummaryCard = () => {
  const record = useRecordContext();
  const { brand } = useBrand();

  if (!record) {
    return null;
  }

  const formatCurrency = (amount: number | undefined | null) => {
    const safeAmount = typeof amount === 'number' && !isNaN(amount) ? amount : 0;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(safeAmount);
  };

  return (
    <Card elevation={2} sx={{ mb: 3 }}>
      <CardContent>
        <Box
          sx={{
            background:
              brand?.theme?.headerGradient || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            p: 3,
            borderRadius: 1,
            mb: 3,
          }}
        >
          <Grid container spacing={3} alignItems="center">
            <Grid item>
              <PaymentIcon sx={{ fontSize: 60, color: 'white' }} />
            </Grid>
            <Grid item xs>
              <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold' }}>
                Payment Details
              </Typography>
              <Typography variant="h5" sx={{ color: 'rgba(255,255,255,0.9)', mt: 1 }}>
                {formatCurrency(record.amount)}
              </Typography>
              <Typography variant="subtitle1" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                {record.paymentDate ? new Date(record.paymentDate).toLocaleDateString() : 'No date'}
              </Typography>
            </Grid>
            <Grid item>
              <PaymentMethodChip record={record} />
            </Grid>
          </Grid>
        </Box>
      </CardContent>
    </Card>
  );
};

const PaymentShowContent = () => {
  const record = useRecordContext();
  const navigate = useNavigate();

  // Get related invoice details
  const { data: invoice } = useGetOne(
    'invoices',
    { id: record?.invoiceId },
    { enabled: !!record?.invoiceId }
  );

  // Get related customer details
  const { data: customer } = useGetOne(
    'customers',
    { id: record?.customerId },
    { enabled: !!record?.customerId }
  );

  if (!record) {
    return null;
  }

  // Format payment amount
  const formatCurrency = (amount: number | undefined | null) => {
    const safeAmount = typeof amount === 'number' && !isNaN(amount) ? amount : 0;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(safeAmount);
  };

  // Format date
  const formatDate = (date: string | undefined) => {
    if (!date) {return '';}
    return new Date(date).toLocaleDateString('en-US', { timeZone: 'UTC' });
  };

  // Build dynamic breadcrumb based on available data
  const breadcrumbItems: BreadcrumbItem[] = [
    {
      label: 'Payments',
      path: '/crm/payments',
      icon: <CreditCardIcon fontSize="small" />,
    },
  ];

  // Add customer breadcrumb if available
  if (customer) {
    breadcrumbItems.push({
      label: customer.displayName,
      path: `/customers/${customer.id}/show`,
      icon: <PersonIcon fontSize="small" />,
    });
  }

  // Add invoice breadcrumb if available
  if (invoice) {
    breadcrumbItems.push({
      label: `Invoice #${invoice.invoiceNumber}`,
      path: `/invoices/${invoice.id}/show`,
      icon: <InvoiceIcon fontSize="small" />,
    });
  }

  // Add payment details as final breadcrumb
  const paymentAmount = formatCurrency(record.amount);
  const paymentDate = formatDate(record.paymentDate);
  const paymentLabel = paymentDate 
    ? `${paymentAmount} (${paymentDate})`
    : paymentAmount;

  breadcrumbItems.push({
    label: paymentLabel,
    icon: <PaymentIcon fontSize="small" />,
  });

  return (
    <Box>
      <PaymentSummaryCard />
      <NavigationBreadcrumbs items={breadcrumbItems} />

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <MoneyIcon color="primary" />
                Payment Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ '& > *': { mb: 2 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DateIcon color="action" />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Payment Date
                    </Typography>
                    <Typography variant="body1">
                      {record.paymentDate
                        ? new Date(record.paymentDate).toLocaleDateString()
                        : 'Not set'}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PaymentIcon color="action" />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Payment Method
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <PaymentMethodChip record={record} />
                    </Box>
                  </Box>
                </Box>

                {record.notes && (
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <NoteIcon color="action" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Notes
                      </Typography>
                      <Typography variant="body2">{record.notes}</Typography>
                    </Box>
                  </Box>
                )}

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DateIcon color="action" />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Recorded At
                    </Typography>
                    <Typography variant="body2">
                      {record.createdAt
                        ? new Date(record.createdAt).toLocaleDateString()
                        : 'Unknown'}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Stack spacing={3}>
            {/* Related Invoice */}
            {invoice && (
              <Card>
                <CardContent>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <InvoiceIcon color="primary" />
                      Related Invoice
                    </Box>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => navigate(`/invoices/${invoice.id}/show`)}
                    >
                      View Invoice
                    </Button>
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Box sx={{ '& > *': { mb: 1 } }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      Invoice #{invoice.invoiceNumber}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {invoice.title}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Total:</strong> ${invoice.totalAmount?.toLocaleString() || '0'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Due Date:</strong>{' '}
                      {invoice.dueDate ? new Date(invoice.dueDate).toLocaleDateString() : 'Not set'}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* Related Customer */}
            {customer && (
              <Card>
                <CardContent>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PersonIcon color="primary" />
                      Customer
                    </Box>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => navigate(`/customers/${customer.id}/show`)}
                    >
                      View Customer
                    </Button>
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Box sx={{ '& > *': { mb: 1 } }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      {customer.displayName}
                    </Typography>
                    {customer.company && (
                      <Typography variant="body2" color="text.secondary">
                        {customer.company}
                      </Typography>
                    )}
                    {customer.email && (
                      <Typography variant="body2">
                        <strong>Email:</strong> {customer.email}
                      </Typography>
                    )}
                    {customer.phone && (
                      <Typography variant="body2">
                        <strong>Phone:</strong> {customer.phone}
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            )}
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};

const PaymentShowWrapper = ({ editDialogOpen, setEditDialogOpen }: { editDialogOpen: boolean; setEditDialogOpen: (open: boolean) => void }) => {
  const record = useRecordContext();

  const handleCloseEdit = () => {
    setEditDialogOpen(false);
  };

  return (
    <>
      <PaymentShowContent />
      
      {record && (
        <PaymentEditDialog 
          open={editDialogOpen}
          onClose={handleCloseEdit}
          record={record}
        />
      )}
    </>
  );
};

export const PaymentShow = () => {
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  const handleEdit = () => {
    setEditDialogOpen(true);
  };

  return (
    <Show actions={<ShowActions onEdit={handleEdit} />}>
      <PaymentShowWrapper editDialogOpen={editDialogOpen} setEditDialogOpen={setEditDialogOpen} />
    </Show>
  );
};
